import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  Stack,
} from "@mui/material";
import {
  Keyboard,
  Close,
  LabelOutlined,
  NavigationOutlined,
  BoltOutlined,
  RefreshOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { TabType, KeyMapItem } from "../../@types/keyboard";
import { useKeyMapping } from "../../hooks/useKeyMapping";
import { projectConfigState } from "../../recoil";
import { useRecoilValue } from "recoil";

interface KeyMappingModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: TabType;
  highlightId?: string;
}

const TAB_META: Record<TabType, { label: string; icon: React.ReactNode; count: number }> = {
  labeling: { label: "라벨링 키", icon: <LabelOutlined sx={{ fontSize: 14 }} />, count: 10 },
  navigation: { label: "내비게이션", icon: <NavigationOutlined sx={{ fontSize: 14 }} />, count: 2 },
  action: { label: "액션", icon: <BoltOutlined sx={{ fontSize: 14 }} />, count: 4 },
};

const TABS: TabType[] = ["labeling", "navigation", "action"];

const ACCENT = "#7c6cf0";
const ACCENT_LIGHT = "#eeedfe";
const ACCENT_BORDER = "#afa9ec";

// 키캡 스타일 컴포넌트
function KeyCap({
  children,
  state = "default",
}: {
  children: React.ReactNode;
  state?: "default" | "listening" | "modified" | "conflict";
}) {
  const colors = {
    default: { bg: "#f4f4f8", border: "#d0d0e0", borderBottom: "#c0c0d8", color: "var(--dk-text-2, #3a3a5c)" },
    listening: { bg: ACCENT_LIGHT, border: ACCENT, borderBottom: "#534ab7", color: "#534ab7" },
    modified: { bg: "#edfaf4", border: "#5dcaa5", borderBottom: "#1d9e75", color: "#085041" },
    conflict: { bg: "#fff0f0", border: "#f09595", borderBottom: "#e24b4a", color: "#791f1f" },
  }[state];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 20,
        height: 22,
        px: "5px",
        borderRadius: "4px",
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderBottom: `2px solid ${colors.borderBottom}`,
        fontSize: 11,
        fontWeight: 700,
        color: colors.color,
        fontFamily: "monospace",
        transition: "all .15s",
        animation: state === "listening" ? "kcap-pulse 1s ease-in-out infinite" : "none",
        "@keyframes kcap-pulse": {
          "0%, 100%": { opacity: 1 },
          "50%": { opacity: 0.5 },
        },
      }}
    >
      {children}
    </Box>
  );
}

function KeyDisplay({
  item,
  isListening,
  isModified,
  isConflict,
}: {
  item: KeyMapItem;
  isListening: boolean;
  isModified: boolean;
  isConflict: boolean;
}) {
  const state = isListening ? "listening" : isConflict ? "conflict" : isModified ? "modified" : "default";

  if (isListening) {
    return (
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: "3px" }}>
        <Box
          sx={{
            position: "absolute",
            top: -22,
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: ACCENT,
            background: ACCENT_LIGHT,
            px: "6px",
            py: "2px",
            borderRadius: "3px",
            border: `0.5px solid ${ACCENT}`,
            animation: "blink .8s step-end infinite",
            "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0 } },
          }}
        >
          키를 누르세요
        </Box>
        <KeyCap state="listening">···</KeyCap>
      </Box>
    );
  }

  const keyLabel = item.modifier
    ? (item.display ?? item.key.toUpperCase()).replace(/^(Ctrl|Alt)\+/i, "")
    : (item.display ?? item.key.toUpperCase());

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
      {item.modifier && (
        <>
          <KeyCap state={state}>{item.modifier}</KeyCap>
          <Box component="span" sx={{ fontSize: 10, color: "var(--dk-text-3, #b0b0c8)" }}>
            +
          </Box>
        </>
      )}
      <KeyCap state={state}>{keyLabel}</KeyCap>
    </Box>
  );
}

function MappingRow({
  item,
  isListening,
  onRowClick,
  onReset,
  getConflict,
}: {
  item: KeyMapItem;
  isListening: boolean;
  onRowClick: (id: string) => void;
  onReset: (id: string) => void;
  getConflict: (id: string, key: string, modifier?: "Ctrl" | "Alt") => KeyMapItem | undefined;
}) {
  const conflict = getConflict(item.id, item.key, item.modifier);
  const isModified = item.key !== item.default;

  const rowBg = isListening ? ACCENT_LIGHT : conflict ? "#fff5f5" : isModified ? "#f4fdf8" : "#fafafa";

  const rowBorder = isListening ? ACCENT : conflict ? "#f09595" : isModified ? "#5dcaa5" : "transparent";

  return (
    <Box
      onClick={() => onRowClick(item.id)}
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 92px 34px",
        alignItems: "center",
        gap: "10px",
        px: "12px",
        py: "9px",
        borderRadius: "8px",
        border: `1px solid ${rowBorder}`,
        background: rowBg,
        cursor: "pointer",
        transition: "all .12s",
        "&:hover": {
          background: isListening ? ACCENT_LIGHT : "#f0f0ff",
          borderColor: isListening ? ACCENT : "#d0d0ee",
        },
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--dk-text, #2a2a4a)", lineHeight: 1.3 }}>{item.action}</Typography>
        <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #9090a8)", mt: "2px", lineHeight: 1 }}>{item.desc}</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minHeight: 30,
          px: "6px",
          py: "5px",
          borderRadius: "6px",
          border: "1px solid var(--dk-border, #e0e0ee)",
          background: "var(--dk-surface, #fff)",
        }}
      >
        <KeyDisplay item={item} isListening={isListening} isModified={isModified} isConflict={!!conflict} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {isModified && !isListening ? (
          <Tooltip title="기본값으로 초기화" placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onReset(item.id);
              }}
              sx={{
                width: 26,
                height: 26,
                borderRadius: "6px",
                border: "1px solid var(--dk-border, #e4e4ef)",
                color: "var(--dk-text-3, #9090a8)",
                "&:hover": { background: "#f5f5f8", color: "var(--dk-text-2, #3a3a5c)" },
              }}
            >
              <RefreshOutlined sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ width: 26 }} />
        )}
      </Box>
    </Box>
  );
}

export function KeyMappingModal({ open, onClose, initialTab, highlightId }: KeyMappingModalProps) {
  const [currentTab, setCurrentTab] = useState<TabType>(initialTab ?? "labeling");
  const [listeningId, setListeningId] = useState<string | null>(null);
  const projectConfig = useRecoilValue(projectConfigState);
  const { config, hasChanges, updateKey, resetItem, resetAll, save, getItemConflict, getTabConflicts } = useKeyMapping(
    projectConfig?.keyConfig || "",
  );

  useEffect(() => {
    if (open && initialTab) setCurrentTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) {
      setListeningId(null);
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (!listeningId) return;
      if (e.key === "Escape") {
        setListeningId(null);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      updateKey(listeningId, e);
      setListeningId(null);
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, listeningId, updateKey]);

  const handleRowClick = useCallback((id: string) => {
    setListeningId((prev) => (prev === id ? null : id));
  }, []);

  const handleSave = useCallback(() => {
    save();
    onClose();
  }, [save, onClose]);

  const tabConflicts = getTabConflicts(currentTab);
  const items = config[currentTab];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          border: "1px solid var(--dk-border, #e0e0ee)",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(80,70,180,0.10)",
        },
      }}
    >
      {/* 헤더 */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "20px",
          py: "14px",
          background: "var(--dk-surface-2, #fafafa)",
          borderBottom: "1px solid #ededf5",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "7px",
              background: ACCENT_LIGHT,
              border: `1px solid ${ACCENT_BORDER}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Keyboard sx={{ fontSize: 15, color: "#534ab7" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--dk-text, #1a1a2e)", lineHeight: 1.2 }}>
              키 매핑 설정
            </Typography>
            <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #9090a8)", mt: "2px" }}>
              기능에 원하는 키를 자유롭게 할당하세요
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            border: "1px solid var(--dk-border, #e4e4ef)",
            color: "var(--dk-text-3, #9090a8)",
            "&:hover": { background: "#f5f5f8", color: "var(--dk-text-2, #3a3a5c)" },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      {/* 탭 */}
      <Box sx={{ background: "var(--dk-surface-2, #fafafa)", borderBottom: "1px solid #ededf5", px: "20px" }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) => {
            setCurrentTab(v);
            setListeningId(null);
          }}
          sx={{
            minHeight: 38,
            "& .MuiTabs-indicator": { backgroundColor: ACCENT, height: 2 },
          }}
        >
          {TABS.map((tab) => {
            const meta = TAB_META[tab];
            const conflicts = getTabConflicts(tab);
            return (
              <Tab
                key={tab}
                value={tab}
                disableRipple
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    {meta.icon}
                    <span>{meta.label}</span>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 10,
                        px: "5px",
                        py: "1px",
                        borderRadius: "3px",
                        background: currentTab === tab ? ACCENT_LIGHT : "#f0f0f8",
                        color: currentTab === tab ? "#534ab7" : "#9090a8",
                      }}
                    >
                      {meta.count}
                    </Box>
                    {conflicts.length > 0 && (
                      <WarningAmberOutlined sx={{ fontSize: 12, color: "#e24b4a", ml: "1px" }} />
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 38,
                  px: "14px",
                  py: 0,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--dk-text-3, #9090a8)",
                  textTransform: "none",
                  "&.Mui-selected": { color: "#5550c8" },
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* 본문 */}
      <DialogContent sx={{ px: "20px", py: "16px", background: "var(--dk-surface, #fff)" }}>
        {tabConflicts.length > 0 && (
          <Alert
            severity="error"
            icon={<WarningAmberOutlined sx={{ fontSize: 15 }} />}
            sx={{
              mb: "12px",
              py: "7px",
              px: "12px",
              fontSize: 12,
              borderRadius: "7px",
              "& .MuiAlert-message": { fontSize: 12 },
            }}
          >
            <strong>키 충돌:</strong> {tabConflicts.map((c) => c.action).join(", ")}에 중복된 키가 있습니다.
          </Alert>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "10px" }}>
          <Typography
            sx={{ fontSize: 10, fontWeight: 600, color: "var(--dk-text-3, #9090a8)", letterSpacing: ".07em", textTransform: "uppercase" }}
          >
            {{ labeling: "라벨링 단축키", navigation: "이미지 내비게이션", action: "편집 액션" }[currentTab]}
          </Typography>
          <Box sx={{ flex: 1, height: "0.5px", background: "#ededf5" }} />
        </Box>

        <Stack spacing="3px">
          {items.map((item) => (
            <MappingRow
              key={item.id}
              item={item}
              isListening={listeningId === item.id}
              onRowClick={handleRowClick}
              onReset={resetItem}
              getConflict={getItemConflict}
            />
          ))}
        </Stack>
      </DialogContent>

      {/* 푸터 */}
      <DialogActions
        sx={{
          px: "20px",
          py: "12px",
          borderTop: "1px solid #ededf5",
          background: "var(--dk-surface-2, #fafafa)",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {[
            ["클릭", "키 변경"],
            ["Esc", "취소"],
          ].map(([key, label]) => (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  px: "5px",
                  py: "1px",
                  borderRadius: "3px",
                  background: "#f0f0f8",
                  border: "0.5px solid #d0d0e0",
                  fontSize: 10,
                  fontWeight: 600,
                  fontFamily: "monospace",
                  color: "#6060808",
                }}
              >
                {key}
              </Box>
              <Typography sx={{ fontSize: 11, color: "var(--dk-text-3, #b0b0c8)" }}>{label}</Typography>
              {key !== "Esc" && <Box sx={{ width: "1px", height: 10, background: "#e0e0ee", mx: "4px" }} />}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: "6px" }}>
          <Button
            size="small"
            onClick={resetAll}
            sx={{
              fontSize: 12,
              px: "12px",
              py: "5px",
              borderRadius: "7px",
              border: "1px solid #f09595",
              color: "#a32d2d",
              textTransform: "none",
              "&:hover": { background: "#fff5f5" },
            }}
          >
            전체 초기화
          </Button>
          <Button
            size="small"
            onClick={onClose}
            sx={{
              fontSize: 12,
              px: "12px",
              py: "5px",
              borderRadius: "7px",
              border: "1px solid #d8d8e8",
              color: "#5a5a7a",
              textTransform: "none",
              "&:hover": { background: "#f5f5f8" },
            }}
          >
            닫기
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!hasChanges || tabConflicts.length > 0}
            onClick={handleSave}
            sx={{
              fontSize: 12,
              px: "14px",
              py: "5px",
              borderRadius: "7px",
              background: ACCENT,
              textTransform: "none",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": { background: "#6b5ce0", boxShadow: "none" },
              "&.Mui-disabled": { opacity: 0.4 },
            }}
          >
            저장
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
